// src/App.jsx — IMA Helpdesk Portal v2 (with React Router)
import React, { useState, useEffect } from "react";
import { BrowserRouter, Routes, Route, NavLink, Navigate, useLocation } from "react-router-dom";
import { useMsal, AuthenticatedTemplate, UnauthenticatedTemplate } from "@azure/msal-react";
import { InteractionStatus } from "@azure/msal-browser";
import { loginRequest } from "./authConfig";
import { getCurrentUser } from "./services/graphService";
import { IncidentsPage }  from "./pages/IncidentsPage";
import { ReportsPage }    from "./pages/ReportsPage";
import { DelegationPage } from "./pages/DelegationPage";
import { DevicesPage }    from "./pages/DevicesPage";
import { MFAPage }        from "./pages/MFAPage";

function IMALogo({ theme }) {
  const src = theme === "dark" ? "/ima-logo-white.png" : "/ima-logo.png";
  return (
    <img src={src} alt="The IMA Group"
      style={{ height: 26, width: "auto", objectFit: "contain" }}
      onError={e => { e.target.src = "/ima-logo.png"; }} />
  );
}

function ThemeToggle({ theme, onToggle }) {
  return (
    <button onClick={onToggle} style={{
      background: "var(--s2)", border: "1px solid var(--b2)",
      borderRadius: 999, padding: "5px 12px", cursor: "pointer",
      display: "flex", alignItems: "center", gap: 6,
      fontSize: 11, color: "var(--tx2)", fontFamily: "var(--sans)",
    }}>
      {theme === "dark" ? "☀ Light" : "☾ Dark"}
    </button>
  );
}

// ─── Login Screen ─────────────────────────────────────────────────────────────
function LoginScreen({ theme, onToggle }) {
  const { instance, inProgress } = useMsal();
  const [loading, setLoading] = useState(false);

  async function handleLogin() {
    setLoading(true);
    try { await instance.loginRedirect(loginRequest); }
    catch (err) { console.error(err); setLoading(false); }
  }

  return (
    <div style={{
      minHeight: "100vh", background: "var(--bg)",
      display: "flex", alignItems: "center", justifyContent: "center",
    }}>
      <div style={{
        position: "fixed", inset: 0, pointerEvents: "none",
        backgroundImage: "linear-gradient(var(--b1) 1px,transparent 1px),linear-gradient(90deg,var(--b1) 1px,transparent 1px)",
        backgroundSize: "40px 40px", opacity: 0.3,
      }}/>
      <div style={{ position: "absolute", top: 16, right: 16 }}>
        <ThemeToggle theme={theme} onToggle={onToggle}/>
      </div>
      <div style={{
        position: "relative", zIndex: 1,
        background: "var(--s1)", border: "1px solid var(--b2)",
        borderRadius: 16, padding: "48px 48px 40px",
        width: 460, textAlign: "center",
        boxShadow: "0 24px 48px rgba(0,0,0,0.3)",
        animation: "fadeIn .3s ease",
      }}>
        <div style={{ display: "flex", justifyContent: "center", marginBottom: 20 }}>
          <IMALogo theme={theme}/>
        </div>
        <div style={{
          display: "inline-block",
          background: "var(--primary-dim)",
          border: "1px solid rgba(0,178,227,0.3)",
          borderRadius: 6, padding: "3px 12px",
          fontSize: 10, color: "var(--primary)",
          fontFamily: "var(--mono)", letterSpacing: ".08em", marginBottom: 20,
        }}>
          HELPDESK PORTAL
        </div>
        <div style={{ fontSize: 12, color: "var(--tx3)", marginBottom: 16, lineHeight: 1.9 }}>
          Incident Response · Ticket Analytics<br/>
          Mailbox Delegation Audit
        </div>
        <div style={{ display: "flex", justifyContent: "center", gap: 24, marginBottom: 32 }}>
          {[
            { label: "Incidents",  dot: "#00B2E3" },
            { label: "Analytics",  dot: "#00BBB4" },
            { label: "IT Audits",  dot: "#6ABF4B" },
            { label: "Devices",    dot: "#8b7ef8" },
            { label: "MFA Audits", dot: "#FF9E18" },
          ].map(({ label, dot }) => (
            <div key={label} style={{ display: "flex", alignItems: "center", gap: 6, fontSize: 11, color: "var(--tx3)" }}>
              <span style={{ width: 7, height: 7, borderRadius: "50%", background: dot, display: "inline-block" }}/>
              {label}
            </div>
          ))}
        </div>
        <button onClick={handleLogin}
          disabled={loading || inProgress !== InteractionStatus.None}
          style={{
            width: "100%", padding: "13px 0",
            background: loading ? "var(--b2)" : "var(--ima-blue)",
            border: "none", borderRadius: "var(--r)",
            color: "#fff", fontFamily: "var(--sans)", fontSize: 13, fontWeight: 600,
            cursor: loading ? "not-allowed" : "pointer",
            display: "flex", alignItems: "center", justifyContent: "center", gap: 10,
            transition: "background .15s",
          }}>
          {loading && <span style={{
            width: 14, height: 14, border: "2px solid rgba(255,255,255,0.4)",
            borderTopColor: "#fff", borderRadius: "50%", animation: "spin .7s linear infinite",
          }}/>}
          {loading ? "Redirecting to Microsoft…" : "Sign in with Microsoft 365"}
        </button>
        <div style={{ marginTop: 14, fontSize: 11, color: "var(--tx3)" }}>
          Only <strong style={{ color: "var(--tx2)" }}>@theimagroup.com</strong> accounts can access this portal.
        </div>
      </div>
    </div>
  );
}

// ─── Nav items matching the requested URLs ────────────────────────────────────
const NAV = [
  { path: "/incidentresponse",   label: "Incident Response",  color: "#00B2E3", icon: "🛡" },
  { path: "/helpdeskanalytics",  label: "Ticket Analytics",   color: "#00BBB4", icon: "📊" },
  { path: "/itaudits",           label: "IT Audits",          color: "#6ABF4B", icon: "🔑" },
  { path: "/devices",            label: "Device Inventory",   color: "#8b7ef8", icon: "🖥" },
  { path: "/mfaaudits",          label: "MFA Audits",          color: "#FF9E18", icon: "🔐" },
];

// ─── Accent bar that tracks active route ─────────────────────────────────────
function AccentBar() {
  const loc = useLocation();
  const idx = NAV.findIndex(n => loc.pathname.startsWith(n.path));
  const active = NAV[idx] || NAV[0];
  return (
    <div style={{ height: 2, background: "var(--b1)", flexShrink: 0 }}>
      <div style={{
        height: "100%",
        background: active.color,
        width: `${(idx + 1) * (100 / NAV.length)}%`,
        transition: "all .3s ease",
      }}/>
    </div>
  );
}

// ─── App Shell ────────────────────────────────────────────────────────────────
function AppShell({ theme, onToggle }) {
  const { instance } = useMsal();
  const [user, setUser] = useState(null);

  useEffect(() => { getCurrentUser().then(setUser).catch(() => {}); }, []);

  const navLinkStyle = ({ isActive }) => ({
    display: "flex", alignItems: "center", gap: 6,
    padding: "6px 13px", borderRadius: "var(--r)",
    border: "1px solid " + (isActive ? "var(--b2)" : "transparent"),
    background: isActive ? "var(--s3)" : "transparent",
    color: isActive ? "var(--tx)" : "var(--tx3)",
    fontFamily: "var(--sans)", fontSize: 11,
    fontWeight: isActive ? 600 : 400,
    cursor: "pointer", whiteSpace: "nowrap",
    transition: "all .15s", textDecoration: "none",
  });

  return (
    <div style={{ display: "flex", flexDirection: "column", height: "100vh", overflow: "hidden" }}>
      {/* Top bar */}
      <div style={{
        height: 54, background: "var(--s1)", borderBottom: "1px solid var(--b1)",
        display: "flex", alignItems: "center", padding: "0 20px", gap: 14,
        flexShrink: 0, zIndex: 10,
      }}>
        <div style={{ flexShrink: 0 }}><IMALogo theme={theme}/></div>
        <div style={{ width: 1, height: 22, background: "var(--b2)", flexShrink: 0 }}/>
        <div style={{
          fontFamily: "var(--mono)", fontSize: 10, color: "var(--tx3)",
          letterSpacing: ".07em", textTransform: "uppercase", flexShrink: 0,
        }}>Helpdesk Portal</div>

        {/* Nav links — these set the URL */}
        <div style={{ display: "flex", gap: 2, flex: 1, overflowX: "auto", marginLeft: 8 }}>
          {NAV.map(n => (
            <NavLink key={n.path} to={n.path} style={navLinkStyle}>
              {({ isActive }) => (
                <>
                  <span style={{ fontSize: 13 }}>{n.icon}</span>
                  <span style={{
                    width: 5, height: 5, borderRadius: "50%",
                    background: isActive ? n.color : "var(--b3)",
                    transition: "background .15s",
                  }}/>
                  {n.label}
                </>
              )}
            </NavLink>
          ))}
        </div>

        {/* User + controls */}
        <div style={{ display: "flex", alignItems: "center", gap: 8, flexShrink: 0 }}>
          <ThemeToggle theme={theme} onToggle={onToggle}/>
          <div style={{
            display: "flex", alignItems: "center", gap: 6,
            padding: "4px 10px", background: "var(--s2)", border: "1px solid var(--b1)",
            borderRadius: 999, fontSize: 11, color: "var(--tx2)", whiteSpace: "nowrap",
          }}>
            <span style={{ width: 5, height: 5, borderRadius: "50%", background: "var(--green)" }}/>
            {user?.mail || "Loading…"}
          </div>
          <button onClick={() => instance.logoutRedirect()} style={{
            background: "transparent", border: "1px solid var(--b2)",
            borderRadius: "var(--r)", color: "var(--tx3)",
            fontFamily: "var(--sans)", fontSize: 11, padding: "5px 10px", cursor: "pointer",
          }}>Sign out</button>
        </div>
      </div>

      <AccentBar/>

      {/* Routed pages */}
      <div style={{ flex: 1, overflow: "hidden" }}>
        <Routes>
          <Route path="/incidentresponse"  element={<IncidentsPage  user={user} theme={theme}/>}/>
          <Route path="/helpdeskanalytics" element={<ReportsPage    user={user} theme={theme}/>}/>
          <Route path="/itaudits"          element={<DelegationPage user={user} theme={theme}/>}/>
          <Route path="/devices"           element={<DevicesPage    user={user} theme={theme}/>}/>
          <Route path="/mfaaudits"         element={<MFAPage        user={user} theme={theme}/>}/>
          {/* Default redirect */}
          <Route path="*" element={<Navigate to="/incidentresponse" replace/>}/>
        </Routes>
      </div>
    </div>
  );
}

// ─── Root ─────────────────────────────────────────────────────────────────────
export default function App() {
  const [theme, setTheme] = useState(() => localStorage.getItem("ima-theme") || "dark");
  useEffect(() => {
    document.documentElement.setAttribute("data-theme", theme);
    localStorage.setItem("ima-theme", theme);
  }, [theme]);
  const toggle = () => setTheme(t => t === "dark" ? "light" : "dark");

  return (
    <BrowserRouter>
      <AuthenticatedTemplate><AppShell theme={theme} onToggle={toggle}/></AuthenticatedTemplate>
      <UnauthenticatedTemplate><LoginScreen theme={theme} onToggle={toggle}/></UnauthenticatedTemplate>
    </BrowserRouter>
  );
}
